// Settings model: ACS server state, optical policy validation and impact,
// settings search. Pure; checked by settings.check.ts.

import { integrationHealth, type IntegrationRow } from "./integrations";
import { classifyRx, type SignalBands } from "./signal";
import type { ACSServer } from "./types";

export type AcsState = "healthy" | "degraded" | "failed" | "disabled" | "unknown";
/** What the device count means: a real count, stale, or nothing synced yet. */
export type AcsDevices = { kind: "count" | "stale" | "never"; n: number | null };

/**
 * One state per ACS server from two signals: the last connection test
 * (server.status) and the sync run (collector_runs "genieacs:<name>").
 * Reachable but failing to sync is DEGRADED, never HEALTHY.
 */
export function acsState(s: ACSServer, run: IntegrationRow | undefined, now: number): { state: AcsState; devices: AcsDevices } {
  const h = run ? integrationHealth(run, now) : null;
  const synced = !!run?.last_ok_at;
  const devices: AcsDevices = !synced ? { kind: "never", n: null }
    : h && (h.state === "stale" || h.state === "failed") ? { kind: "stale", n: s.device_count }
    : { kind: "count", n: s.device_count };
  if (!s.enabled) return { state: "disabled", devices };
  if (s.status === "error") return { state: "failed", devices };
  if (!h || h.state === "unknown") return { state: s.status === "ok" && synced ? "healthy" : "unknown", devices };
  if (h.state === "failed" || h.state === "stale" || h.state === "degraded") return { state: "degraded", devices };
  return { state: "healthy", devices };
}

/** Overload is a device limit (signal.ts), so a policy band above it is meaningless. */
const OVERLOAD_DBM = -8;
const FLOOR_DBM = -40;

export type PolicyError = "number" | "order" | "range" | null;

/** Critical must sit strictly below warning, both inside the receiver's range. */
export function policyError(warn: string, crit: string): PolicyError {
  const w = Number(warn), c = Number(crit);
  if (warn.trim() === "" || crit.trim() === "" || !Number.isFinite(w) || !Number.isFinite(c)) return "number";
  if (w > OVERLOAD_DBM || c < FLOOR_DBM) return "range";
  if (c >= w) return "order";
  return null;
}

export type Impact = { good: number; warn: number; crit: number; unknown: number };

/** How many ONTs each band would hold under `bands`: the consequence of a change. */
export function impact(rx: (number | null)[], bands: SignalBands): Impact {
  const out: Impact = { good: 0, warn: 0, crit: 0, unknown: 0 };
  for (const v of rx) out[classifyRx(v, bands)]++;
  return out;
}

/** Three readings that land in each band of a policy, for the preview. */
export const previewSamples = (b: SignalBands) => [b.warn + 1.6, (b.warn + b.crit) / 2, b.crit - 1.1].map((v) => Math.round(v * 10) / 10);

export type Section = "overview" | "general" | "monitoring" | "integrations" | "access" | "sharing" | "advanced";
export type SearchEntry = { section: Section; anchor: string; label: string; terms: string };

/** Entries whose label or search terms contain every word of the query. */
export function searchSettings(index: SearchEntry[], q: string): SearchEntry[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return index.filter((e) => {
    const hay = `${e.label} ${e.terms}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** "103.148.76.159:7557" from an NBI base URL; the raw string when it does not parse. */
export function endpointOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
